import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/apiClient";
import { Github, Globe, Server, Loader2, Check, Trash2, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-xs text-neutral-400 mb-1.5">{label}</label>
      <input
        {...props}
        className="w-full h-10 rounded-md border border-[#262626] bg-[#141414] px-3 text-sm outline-none focus:ring-2 focus:ring-neutral-500"
      />
    </div>
  );
}

function ConnectionCard({ icon: Icon, title, desc, docsUrl, connected, valid, error, meta, children, onRevoke, testid }) {
  const needsReconnect = connected && valid === false;
  const showForm = !connected || needsReconnect;
  return (
    <div className="rounded-lg border border-[#262626] bg-[#141414] p-6" data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-[#1F1F1F] border border-[#262626] flex items-center justify-center">
            <Icon className="h-5 w-5 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-heading text-lg font-medium tracking-tight">{title}</h3>
            <p className="text-xs text-neutral-500">{desc}</p>
          </div>
        </div>
        {needsReconnect ? (
          <span data-testid={`${testid}-status-reconnect`} className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-0.5 text-xs font-semibold text-amber-400">
            <AlertTriangle className="h-3 w-3" /> Reconnect needed
          </span>
        ) : connected ? (
          <span data-testid={`${testid}-status-connected`} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-semibold text-emerald-400">
            <Check className="h-3 w-3" /> Connected
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full border border-neutral-700 bg-neutral-800/50 px-2.5 py-0.5 text-xs font-semibold text-neutral-400">
            Not connected
          </span>
        )}
      </div>

      {needsReconnect && (
        <div className="mb-4 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs text-amber-300/90" data-testid={`${testid}-reconnect-notice`}>
          Your saved token was rejected{error ? ` (${error})` : ""}. Generate a fresh token and reconnect below.
        </div>
      )}

      {connected && !needsReconnect ? (
        <div className="flex items-center justify-between">
          <div className="text-sm text-neutral-300 font-mono truncate">
            {meta || "Credentials stored (encrypted)"}
          </div>
          <button
            onClick={onRevoke}
            data-testid={`${testid}-revoke`}
            className="inline-flex items-center gap-1.5 rounded-md border border-[#262626] px-3 py-1.5 text-xs text-red-400 hover:bg-[#1F1F1F] transition-colors duration-200"
          >
            <Trash2 className="h-3.5 w-3.5" /> Revoke
          </button>
        </div>
      ) : showForm ? (
        <div className="space-y-3">
          {children}
          {docsUrl && (
            <a href={docsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-white transition-colors duration-200">
              Where do I get this? <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function Connections() {
  const [conns, setConns] = useState(null);
  const [health, setHealth] = useState(null);
  const [gh, setGh] = useState("");
  const [nl, setNl] = useState("");
  const [ncUser, setNcUser] = useState("");
  const [ncToken, setNcToken] = useState("");
  const [ncEnv, setNcEnv] = useState("sandbox");
  const [busy, setBusy] = useState("");

  const load = () =>
    api.get("/connections").then((r) => setConns(r.data)).catch(() => {});

  const verify = () => {
    setHealth(null);
    api.get("/connections/verify").then((r) => setHealth(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    verify();
  }, []);

  const save = async (provider, body, resetFn) => {
    setBusy(provider);
    try {
      await api.post(`/connections/${provider}`, body);
      toast.success(`${provider} connected`);
      resetFn && resetFn();
      await load();
      verify();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const revoke = async (provider) => {
    try {
      await api.delete(`/connections/${provider}`);
      toast.success(`${provider} revoked`);
      await load();
      verify();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tighter">Connections</h1>
        <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
          Bring your own credentials. Tokens are encrypted at rest, never sent back to the browser, and revocable anytime.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ConnectionCard
          testid="conn-github"
          icon={Github}
          title="GitHub"
          desc="Personal access token · repo scope"
          docsUrl="https://github.com/settings/tokens"
          connected={conns?.github?.connected}
          valid={health?.github?.valid}
          error={health?.github?.error}
          meta={conns?.github?.meta?.login && `@${conns.github.meta.login}`}
          onRevoke={() => revoke("github")}
        >
          <Field label="Personal Access Token" type="password" placeholder="ghp_..." value={gh} onChange={(e) => setGh(e.target.value)} data-testid="github-token-input" />
          <button
            onClick={() => save("github", { token: gh }, () => setGh(""))}
            disabled={busy === "github" || gh.length < 10}
            data-testid="github-connect-button"
            className="w-full h-10 rounded-md bg-white text-black text-sm font-medium hover:bg-neutral-200 transition-colors duration-200 flex items-center justify-center disabled:opacity-50"
          >
            {busy === "github" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect GitHub"}
          </button>
        </ConnectionCard>

        <ConnectionCard
          testid="conn-netlify"
          icon={Globe}
          title="Netlify"
          desc="Personal access token"
          docsUrl="https://app.netlify.com/user/applications#personal-access-tokens"
          connected={conns?.netlify?.connected}
          valid={health?.netlify?.valid}
          error={health?.netlify?.error}
          meta={conns?.netlify?.meta?.email}
          onRevoke={() => revoke("netlify")}
        >
          <Field label="Personal Access Token" type="password" placeholder="nfp_..." value={nl} onChange={(e) => setNl(e.target.value)} data-testid="netlify-token-input" />
          <button
            onClick={() => save("netlify", { token: nl }, () => setNl(""))}
            disabled={busy === "netlify" || nl.length < 10}
            data-testid="netlify-connect-button"
            className="w-full h-10 rounded-md bg-white text-black text-sm font-medium hover:bg-neutral-200 transition-colors duration-200 flex items-center justify-center disabled:opacity-50"
          >
            {busy === "netlify" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect Netlify"}
          </button>
        </ConnectionCard>

        <ConnectionCard
          testid="conn-namecom"
          icon={Server}
          title="Name.com"
          desc="API username + token · Core v1"
          docsUrl="https://www.name.com/account/settings/api"
          connected={conns?.namecom?.connected}
          valid={health?.namecom?.valid}
          error={health?.namecom?.error}
          meta={conns?.namecom?.connected && `${conns.namecom.meta?.username} · ${conns.namecom.env}`}
          onRevoke={() => revoke("namecom")}
        >
          <Field label="API Username" placeholder="username-test" value={ncUser} onChange={(e) => setNcUser(e.target.value)} data-testid="namecom-username-input" />
          <Field label="API Token" type="password" placeholder="token" value={ncToken} onChange={(e) => setNcToken(e.target.value)} data-testid="namecom-token-input" />
          <div>
            <label className="block text-xs text-neutral-400 mb-1.5">Environment</label>
            <div className="grid grid-cols-2 gap-2">
              {["sandbox", "production"].map((env) => (
                <button
                  key={env}
                  onClick={() => setNcEnv(env)}
                  data-testid={`namecom-env-${env}`}
                  className={`h-9 rounded-md border text-xs font-medium transition-colors duration-200 ${ncEnv === env ? "border-white bg-white text-black" : "border-[#262626] text-neutral-300 hover:bg-[#1F1F1F]"}`}
                >
                  {env === "sandbox" ? "Sandbox (-test)" : "Production"}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => save("namecom", { username: ncUser, token: ncToken, env: ncEnv }, () => { setNcUser(""); setNcToken(""); })}
            disabled={busy === "namecom" || ncUser.length < 2 || ncToken.length < 5}
            data-testid="namecom-connect-button"
            className="w-full h-10 rounded-md bg-white text-black text-sm font-medium hover:bg-neutral-200 transition-colors duration-200 flex items-center justify-center disabled:opacity-50"
          >
            {busy === "namecom" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect Name.com"}
          </button>
        </ConnectionCard>
      </div>
    </Layout>
  );
}
